// Sked's public status page is hosted by Hund.io. The live SSE feed is public
// and CORS-open (`access-control-allow-origin: *`), so the browser talks to it
// directly — there is no server proxy in front of it.
//
// The host is a constant on purpose: every environment (local, staging, prod)
// points at the one real feed, because a status page only ever describes
// production. There is nothing to swap per environment.
export const STATUS_FEED_HOST = "https://status.skedsocial.com";
export const STATUS_FEED_URL = `${STATUS_FEED_HOST}/live/v2/status_page`;
export const STATUS_PAGE_URL = STATUS_FEED_HOST;

/** Public page for a single incident. */
export function statusIssueUrl(issueId: string): string {
  return `${STATUS_FEED_HOST}/issues/${issueId}`;
}

// Reconnect backoff. Hund's own `retry: 300` is far too eager for a feed we
// only need eventual consistency from, so we close the stream on error and
// reopen it ourselves: 5s → 10s → 20s … capped at 5 minutes.
export const RECONNECT_BASE_DELAY_MS = 5000;
export const RECONNECT_MAX_DELAY_MS = 5 * 60 * 1000;
export const RECONNECT_BACKOFF_FACTOR = 2;

// Scheduled maintenance surfaces as a blue dot once it is active or starts
// within this window. Earlier than that it is noise.
export const MAINTENANCE_LEAD_MS = 48 * 60 * 60 * 1000;

/**
 * Every named event Hund emits on this stream, matching `hundio/status.js`
 * (`src/status.coffee`). These are NAMED SSE events — each needs its own
 * `addEventListener`; nothing arrives on the generic `message` channel.
 *
 * Overall state and issues are maintained independently: the three "basic"
 * events carry only a new overall `state`, the issue events only mutate the
 * issue list. See `reduceFeedEvent` for the per-event semantics.
 */
export const SNAPSHOT_EVENT = "init_event";
export const FEED_EVENTS = [
  SNAPSHOT_EVENT,
  // basic events — overall state only
  "status_created",
  "degraded",
  "restored",
  // issue events — issue list only
  "issue_created",
  "issue_started",
  "issue_updated",
  "issue_resolved",
  "issue_cancelled",
  "issue_ended",
  "issue_reopened",
] as const;

export type FeedEventName = (typeof FEED_EVENTS)[number];

// Session-scoped banner dismissal. Keyed by the active incident, so a new or
// escalated incident re-shows the banner even after a dismiss.
export const BANNER_DISMISS_STORAGE_KEY = "sked.service-status.dismissed";
